import { useMemo } from 'react'
import { useConsumerCart, useQueryParamsFulfillment } from 'shop/hooks'
import { useParams } from 'react-router-dom'
import { compareDatetimes } from 'shop/utils'

/** Cart is a match if *all* of the following are met:
 * - cart exists
 * - cart’s `store.slug` matches the slug in the URL
 * - cart's `fulfilment.type` matches the "fulfilment_type" query parameter (if provided)
 * - cart's `fulfilment.window.from` date matches the "date" query parameter (if provided)
 * - cart's `fulfilment.window.from` time matches the "time" query parameter (if provided) */
export const useCartMatch = () => {
  const { cart } = useConsumerCart()
  const { fulfillmentQueryParams } = useQueryParamsFulfillment()
  const params = useParams<{ slug: string }>()

  const urlSlug = params.slug

  const fulfillmentTypeParam = fulfillmentQueryParams.fulfillmentType

  const isCartMatch = useMemo(() => {
    if (!cart || !cart.store || !cart.store.slug || !urlSlug) {
      return false
    }

    if (cart.store.slug !== urlSlug) {
      return false
    }

    // compare with query params
    if (!!fulfillmentQueryParams) {
      const {
        fulfillmentType,
        fulfillmentDate,
        fulfillmentTime,
        fulfillmentDatetime
      } = fulfillmentQueryParams
      // check fulfillmentType
      if (fulfillmentType) {
        if (!cart.fulfillment || cart.fulfillment.type !== fulfillmentType) {
          return false
        }
      }
      // check date and time
      if (fulfillmentDatetime && cart.fulfillment.window?.from) {
        const { isDateEqual, isTimeEqual } = compareDatetimes(
          fulfillmentDatetime,
          cart.fulfillment.window.from
        )

        if (!!fulfillmentDate && !isDateEqual) return false
        if (!!fulfillmentTime && !isTimeEqual) return false
      }
    }

    return true
  }, [cart, urlSlug, fulfillmentTypeParam, fulfillmentQueryParams])

  return {
    isCartMatch
  }
}
